<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container payroll-attendance-report">
      <div class="payroll-attendance-report-header">
        <div>
          <h6 class="oxd-text oxd-text--h6 orangehrm-main-title">
            Attendance Reports
          </h6>
          <div class="payroll-attendance-report-tabs" aria-label="Attendance reports">
            <button
              type="button"
              class="payroll-attendance-report-tab"
              :class="{active: activeReport === 'monthly'}"
              @click="selectReport('monthly')"
            >
              Monthly Report
            </button>
            <button
              type="button"
              class="payroll-attendance-report-tab"
              :class="{active: activeReport === 'dailyHours'}"
              @click="selectReport('dailyHours')"
            >
              Daily Hours Report
            </button>
            <button
              type="button"
              class="payroll-attendance-report-tab"
              :class="{active: activeReport === 'totalHours'}"
              @click="selectReport('totalHours')"
            >
              Hours Monthly Report
            </button>
            <button
              type="button"
              class="payroll-attendance-report-tab"
              :class="{active: activeReport === 'punchLocation'}"
              @click="selectReport('punchLocation')"
            >
              Punch Location Report
            </button>
            <button
              type="button"
              class="payroll-attendance-report-tab"
              :class="{active: activeReport === 'movementTrace'}"
              @click="selectReport('movementTrace')"
            >
              Movement Trace Report
            </button>
            <button
              type="button"
              class="payroll-attendance-report-tab"
              :class="{active: activeReport === 'advanced'}"
              @click="selectReport('advanced')"
            >
              Advanced Reports
            </button>
          </div>
        </div>
        <a
          class="oxd-button oxd-button--medium oxd-button--secondary"
          :href="activeReportUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Full Page
        </a>
      </div>

      <iframe
        class="payroll-attendance-report-frame"
        :title="activeReportTitle"
        :src="activeEmbeddedReportUrl"
      ></iframe>
    </div>
  </div>
</template>

<script>
import {computed, ref} from 'vue';

export default {
  setup() {
    const reportKeys = [
      'monthly',
      'dailyHours',
      'totalHours',
      'punchLocation',
      'movementTrace',
      'advanced',
    ];
    const requestedReport = new URLSearchParams(window.location.search).get('report');
    const activeReport = ref(
      reportKeys.includes(requestedReport) ? requestedReport : 'monthly',
    );
    const basePath = computed(() => {
      const appBaseUrl = window.appGlobal?.baseUrl || '';
      const sourcePath = appBaseUrl || window.location.pathname;
      const webIndex = sourcePath.indexOf('/web/index.php');
      if (webIndex !== -1) {
        return `${sourcePath.slice(0, webIndex)}/web`;
      }

      if (sourcePath === '/web' || sourcePath.endsWith('/web')) {
        return sourcePath;
      }

      const webFolderIndex = sourcePath.indexOf('/web/');
      return webFolderIndex === -1
        ? ''
        : `${sourcePath.slice(0, webFolderIndex)}/web`;
    });

    const reportUrls = computed(() => {
      return {
        monthly: `${basePath.value}/monthly-attendance-report.php`,
        dailyHours: `${basePath.value}/monthly-hours-grid-report.php`,
        totalHours: `${basePath.value}/total-hours-monthly-report.php`,
        punchLocation: `${basePath.value}/punch-location-report.php`,
        movementTrace: `${basePath.value}/group-hr-zkteco-movement.php`,
        advanced: `${basePath.value}/advanced-attendance-reports.php`,
      };
    });

    const activeReportUrl = computed(() => {
      return reportUrls.value[activeReport.value];
    });

    const activeEmbeddedReportUrl = computed(() => {
      return `${activeReportUrl.value}?embedded=1`;
    });

    const activeReportTitle = computed(() => {
      if (activeReport.value === 'totalHours') {
        return 'Hours Monthly Report';
      }
      if (activeReport.value === 'dailyHours') {
        return 'Daily Hours Monthly Report';
      }
      if (activeReport.value === 'punchLocation') {
        return 'Punch Location Report';
      }
      if (activeReport.value === 'movementTrace') {
        return 'Employee IN / OUT Movement Trace';
      }
      if (activeReport.value === 'advanced') {
        return 'Advanced Attendance Reports';
      }
      return 'Monthly Attendance Report for Payroll';
    });

    const selectReport = (report) => {
      if (!reportKeys.includes(report)) {
        return;
      }
      activeReport.value = report;
      const url = new URL(window.location.href);
      url.searchParams.set('report', report);
      window.history.replaceState({}, '', url.toString());
    };

    return {
      activeReport,
      activeReportUrl,
      activeEmbeddedReportUrl,
      activeReportTitle,
      selectReport,
    };
  },
};
</script>

<style lang="scss" scoped>
.payroll-attendance-report {
  padding: 0;
  overflow: hidden;
}

.payroll-attendance-report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem;
  border-bottom: 1px solid #e8eaef;
}

.payroll-attendance-report-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.payroll-attendance-report-tab {
  min-height: 2.1rem;
  border: 1px solid #dfe3e8;
  border-radius: 0.4rem;
  background: #ffffff;
  color: #38455d;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0 0.8rem;
}

.payroll-attendance-report-tab.active {
  background: var(--oxd-primary-one-color);
  border-color: var(--oxd-primary-one-color);
  color: #ffffff;
}

.payroll-attendance-report-frame {
  display: block;
  width: 100%;
  height: calc(100vh - 250px);
  min-height: 560px;
  border: 0;
  background: #fff;
}

@media (max-width: 800px) {
  .payroll-attendance-report-header {
    align-items: stretch;
    flex-direction: column;
  }

  .payroll-attendance-report-frame {
    height: calc(100vh - 310px);
  }
}
</style>
